"use client";

import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { getBootstrapSlice } from "@/lib/studio/bootstrap-client";
import {
  resolveVoiceModelForNewGeneration,
  type VoiceModelId,
} from "@/lib/voice/model-registry";

/**
 * User Preferences
 *
 * These are stored both locally (for fast access) and on the server (for sync across devices).
 * The schema is flexible - adding new preferences here will automatically:
 * 1. Use the default value until the user changes it
 * 2. Be saved to the server when changed
 * 3. Not break existing users (missing keys get defaults)
 *
 * STRUCTURE:
 * - Top-level: General app preferences
 * - Nested objects: Feature-specific settings (e.g., brainstorm, playground)
 */

// ============================================
// Brainstorm Feature Settings
// ============================================
export type AIModel =
  | "claude-sonnet-4.5"
  | "claude-opus-5"
  | "gemini-3-flash"
  | "gemini-2.5-flash"
  | "gemini-3-pro"
  | "gemini-3-pro-fast";

/**
 * Any ElevenLabs model id. Legacy ids (v3, Multilingual v2, Turbo v2.5,
 * Flash v2.5) stay in the union because they may be stored, but they are
 * resolved to Eleven v4 before use (see brainstormSettingsAtom).
 */
export type TTSModel = VoiceModelId;

export type VoicePersona = "alfred" | "lidija" | "jarvis";

export type PresetMode = "speed" | "quality" | "custom";

/**
 * Microphone sensitivity levels for VAD (Voice Activity Detection)
 * Controls how long the system waits after silence before committing transcript
 * - low: Fast/aggressive - commits quickly after short pauses (default)
 * - medium: Moderate - allows for thinking pauses
 * - high: Relaxed - waits longer for deliberate speakers
 */
export type MicSensitivity = "low" | "medium" | "high";

/**
 * Voice input mode
 * - auto: VAD auto-detects when user stops speaking (with pause capability)
 * - push-to-talk: Hold button to record, release to send (for noisy environments)
 */
export type InputMode = "auto" | "push-to-talk";

/**
 * Image generation model for brainstorm concepts
 */
export type ImageModel =
  | "flux-2"
  | "klein-4b"
  | "nano-banana"
  | "nano-banana-2"
  | "nano-banana-pro"
  | "gpt-image-2"
  | "gpt-image-2.5-flare"
  | "gpt-image-2.5-sunburst"
  | "z-turbo"
  | "seedream"
  | "seedream-5-pro"
  | "runware-zimageturbo"
  | "runware-pony"
  | "runware-flux";

/**
 * Supported aspect ratios for image generation models
 */
export type ImageAspectRatio =
  | "1:1"
  | "4:3"
  | "3:4"
  | "16:9"
  | "9:16"
  | "21:9"
  | "3:2"
  | "2:3";

/**
 * Image model display info with supported aspect ratios
 */
export const IMAGE_MODEL_INFO: Record<
  ImageModel,
  {
    name: string;
    credits: number;
    description: string;
    supportedAspectRatios: ImageAspectRatio[];
    defaultAspectRatio: ImageAspectRatio;
  }
> = {
  "flux-2": {
    name: "Flux-2",
    credits: 15,
    description: "High quality, balanced speed",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16", "21:9"],
    defaultAspectRatio: "9:16",
  },
  "klein-4b": {
    name: "Flux Klein 4B",
    credits: 8,
    description: "Efficient, fast generation",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16", "21:9"],
    defaultAspectRatio: "9:16",
  },
  "nano-banana": {
    name: "Nano Banana",
    credits: 70,
    description: "Fast generation",
    supportedAspectRatios: [
      "1:1",
      "4:3",
      "3:4",
      "16:9",
      "9:16",
      "21:9",
      "3:2",
      "2:3",
    ],
    defaultAspectRatio: "9:16",
  },
  "nano-banana-2": {
    name: "Nano Banana 2",
    credits: 70,
    description: "Gemini 3.1 Flash image preview",
    supportedAspectRatios: [
      "1:1",
      "4:3",
      "3:4",
      "16:9",
      "9:16",
      "21:9",
      "3:2",
      "2:3",
    ],
    defaultAspectRatio: "9:16",
  },
  "nano-banana-pro": {
    name: "Nano Banana Pro",
    credits: 130,
    description: "Premium quality",
    supportedAspectRatios: [
      "1:1",
      "4:3",
      "3:4",
      "16:9",
      "9:16",
      "21:9",
      "3:2",
      "2:3",
    ],
    defaultAspectRatio: "9:16",
  },
  "gpt-image-2": {
    name: "GPT Image 2",
    credits: 130,
    description: "Detailed images with fine typography",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    defaultAspectRatio: "4:3",
  },
  "gpt-image-2.5-flare": {
    name: "GPT Image 2.5 Flare",
    credits: 85,
    description: "OpenAI's latest — detailed images, fine typography",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    defaultAspectRatio: "4:3",
  },
  "gpt-image-2.5-sunburst": {
    name: "GPT Image 2.5 Sunburst",
    credits: 85,
    description: "GPT Image 2.5's higher-quality variant",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    defaultAspectRatio: "4:3",
  },
  "z-turbo": {
    name: "Z-Turbo",
    credits: 8,
    description: "Fast, good quality",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    defaultAspectRatio: "9:16",
  },
  seedream: {
    name: "Seedream v4.5",
    credits: 35,
    description: "ByteDance unified generation",
    supportedAspectRatios: [
      "1:1",
      "4:3",
      "3:4",
      "16:9",
      "9:16",
      "21:9",
      "3:2",
      "2:3",
    ],
    defaultAspectRatio: "9:16",
  },
  "seedream-5-pro": {
    name: "Seedream 5 Pro",
    credits: 60,
    description: "Deep prompt understanding and precise text layouts",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    defaultAspectRatio: "9:16",
  },
  "runware-zimageturbo": {
    name: "Runware ZImageTurbo",
    credits: 8,
    description: "Fast LoRA-optimized generation",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    defaultAspectRatio: "9:16",
  },
  "runware-pony": {
    name: "Runware Pony",
    credits: 15,
    description: "Pony Diffusion with LoRA support",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    defaultAspectRatio: "9:16",
  },
  "runware-flux": {
    name: "Runware Flux",
    credits: 25,
    description: "Flux.1 Dev with LoRA support",
    supportedAspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16", "21:9"],
    defaultAspectRatio: "9:16",
  },
};

/**
 * VAD settings for each sensitivity level
 */
export const MIC_SENSITIVITY_SETTINGS = {
  low: {
    vadSilenceThresholdSecs: 0.31, // Minimum allowed
    minSilenceDurationMs: 250,
    label: "Fast",
    description: "Quick responses, minimal pauses",
  },
  medium: {
    vadSilenceThresholdSecs: 0.8,
    minSilenceDurationMs: 600,
    label: "Medium",
    description: "Allows thinking pauses",
  },
  high: {
    vadSilenceThresholdSecs: 1.5,
    minSilenceDurationMs: 1000,
    label: "Relaxed",
    description: "Extended pauses for deliberate speakers",
  },
} as const;

export interface BrainstormSettings {
  aiModel: AIModel;
  ttsModel: TTSModel;
  voicePersona: VoicePersona;
  presetMode: PresetMode;
  micSensitivity: MicSensitivity;
  inputMode: InputMode;
  imageModel: ImageModel;
}

export const MODEL_PRESETS = {
  speed: {
    aiModel: "gemini-3-flash" as AIModel,
    ttsModel: "eleven_v4" as TTSModel,
  },
  quality: {
    aiModel: "claude-sonnet-4.5" as AIModel,
    ttsModel: "eleven_v4" as TTSModel,
  },
} as const;

export const DEFAULT_BRAINSTORM_SETTINGS: BrainstormSettings = {
  aiModel: MODEL_PRESETS.speed.aiModel,
  ttsModel: MODEL_PRESETS.speed.ttsModel,
  voicePersona: "alfred",
  presetMode: "speed",
  micSensitivity: "low",
  inputMode: "auto",
  imageModel: "gpt-image-2.5-flare",
};

// ============================================
// Canvas Aspect Ratio Setting
// ============================================
/**
 * Default canvas aspect ratio for new nodes
 * - "16:9": Landscape (default)
 * - "9:16": Portrait/Vertical
 * - "auto": Auto-detect from source or use 16:9
 */
export type CanvasAspectRatio = "16:9" | "9:16" | "auto";

// ============================================
// Main Preferences Interface
// ============================================
export type ThemeStyle = "legacy" | "new";

export const THEME_COOKIE_NAME = "multic-theme-style";
export const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 400; // ~13 months

export interface UserPreferences {
  // General app preferences
  showCreditsInSidebar: boolean;
  performanceMode: boolean;
  snapToGrid: boolean;
  rememberAssetFilters: boolean;
  showPromptBelowImage: boolean;
  simplifiedSidebar: boolean;
  themeStyle: ThemeStyle;
  dashboardV2: boolean;

  // Canvas/Playground preferences
  focusNewNodes: boolean; // Auto-center viewport on new nodes
  autoConnectNewNodes: boolean; // Auto-connect new nodes to selected nodes
  autoArrangeOnlyConnected: boolean; // Story canvas auto-arrange: only move nodes attached to 2+ nodes, leave loosely connected nodes in place
  autoPlayVideosOnCanvas: boolean; // Auto-play videos when they appear on canvas
  defaultCanvasAspectRatio: CanvasAspectRatio; // Default aspect ratio for new canvas sessions
  collapseSidebarOnCanvas: boolean; // Collapse sidebar when opening Canvas
  lastEditMode: "image" | "video"; // Last toggle on the Edit node — restored when spawning a new one
  // Per-node default models — set explicitly in settings or auto-tracked from
  // the most recent submission. Stored as plain strings so adding a new model
  // doesn't require a schema/migration change. Validated at spawn time.
  defaultImageGenModel: string;
  defaultVideoProvider: string;
  defaultEditProvider: string;
  // "Voice for drafts" (voice programme R11/DV6): the voice model that reads
  // lines with no take yet. Any selectable voice model id; resolved with
  // `resolveDraftVoiceModel` (lib/voice/draft-voice.ts).
  draftVoiceModel: string;

  // Playtest & Publishing quality
  // When true, playtests stream the raw source clips (runtime-trimmed) with no
  // pre-processing — instant to start, but higher bandwidth and no mobile
  // H.265. Default false: derived clips + H.265 variants are locked in at
  // playtest kickoff so a paired phone gets the same mobile-launchable quality
  // it will ship with.
  useOriginalsForPlaytest: boolean;
  // When true, publishing does NOT block on clip/variant preparation — it
  // ships immediately and the derived clips + H.265 encode in the background.
  // Faster to publish; the story is briefly served on originals on mobile
  // until variants land. Default false: prepare/verify mobile quality before
  // the story goes live.
  fastPublishing: boolean;

  // Feature-specific settings (nested for organization)
  brainstorm: BrainstormSettings;
}

/**
 * Default preferences - MUST match the server-side defaults
 * When adding new preferences, add them here with sensible defaults
 */
export const DEFAULT_PREFERENCES: UserPreferences = {
  // General
  showCreditsInSidebar: true,
  performanceMode: false,
  snapToGrid: false,
  rememberAssetFilters: true,
  showPromptBelowImage: false,
  simplifiedSidebar: false,
  themeStyle: "new",
  dashboardV2: true,

  // Canvas/Playground
  focusNewNodes: true, // Auto-center on new nodes by default
  autoConnectNewNodes: true, // Auto-connect new nodes to selected nodes by default
  autoArrangeOnlyConnected: true, // Only auto-arrange multi-connected nodes by default (avoid coalescing loose nodes)
  autoPlayVideosOnCanvas: true, // Auto-play videos on canvas by default
  defaultCanvasAspectRatio: "9:16", // Vertical-first studio — 9:16 default
  collapseSidebarOnCanvas: true, // Collapse sidebar when opening Canvas by default
  lastEditMode: "image", // First-time users get image edit by default
  defaultImageGenModel: "gpt-image-2.5-flare",
  defaultVideoProvider: "fal",
  defaultEditProvider: "nano-banana-pro-fal",
  draftVoiceModel: "eleven_v4",

  // Playtest & Publishing — default to preparing mobile-launchable quality
  // (locked clips + H.265) rather than fast/originals shortcuts.
  useOriginalsForPlaytest: false,
  fastPublishing: false,

  // Feature settings
  brainstorm: DEFAULT_BRAINSTORM_SETTINGS,
};

/**
 * Local storage atom for fast access and offline support
 * This provides instant UI updates while server sync happens in background
 */
export const preferencesAtom = atomWithStorage<UserPreferences>(
  "multic-preferences",
  DEFAULT_PREFERENCES,
);

/**
 * Sync state atom - tracks if preferences are synced with server
 */
export const preferencesSyncStateAtom = atom<{
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
}>({
  isLoading: false,
  isLoaded: false,
  error: null,
});

/**
 * Action atom to load preferences from server and merge with local
 */
export const loadPreferencesFromServerAtom = atom(null, async (_get, set) => {
  set(preferencesSyncStateAtom, {
    isLoading: true,
    isLoaded: false,
    error: null,
  });

  try {
    // First load: take the slice from the consolidated bootstrap request;
    // fall back to the endpoint if bootstrap missed it.
    const boot = await getBootstrapSlice<{
      success: boolean;
      preferences: UserPreferences;
    }>("preferences");

    let data: { success?: boolean; preferences?: UserPreferences };
    if (boot) {
      data = boot;
    } else {
      const response = await fetch("/api/user/preferences");
      if (!response.ok) {
        throw new Error("Failed to fetch preferences");
      }
      data = await response.json();
    }

    if (data.success && data.preferences) {
      // Deep merge server preferences with defaults to preserve new local-only fields
      const serverPrefs = data.preferences;
      const merged: UserPreferences = {
        ...DEFAULT_PREFERENCES,
        ...serverPrefs,
        // Deep merge brainstorm settings to preserve newer fields like inputMode
        brainstorm: {
          ...DEFAULT_BRAINSTORM_SETTINGS,
          ...(serverPrefs.brainstorm || {}),
        },
      };
      set(preferencesAtom, merged);
    }

    set(preferencesSyncStateAtom, {
      isLoading: false,
      isLoaded: true,
      error: null,
    });
  } catch (error) {
    console.error("Error loading preferences from server:", error);
    set(preferencesSyncStateAtom, {
      isLoading: false,
      isLoaded: true, // Mark as loaded so we don't keep retrying
      error: error instanceof Error ? error.message : "Unknown error",
    });
  }
});

/**
 * Debounced server sync
 * Batches rapid changes and only sends one request after user stops changing
 */
let pendingUpdates: Partial<UserPreferences> = {};
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 2000;

async function flushPendingUpdates() {
  if (Object.keys(pendingUpdates).length === 0) return;

  const updates = { ...pendingUpdates };
  pendingUpdates = {};

  // Extra safety check - ensure updates is not empty after stringify
  const body = JSON.stringify(updates);
  if (!body || body === "{}" || body === "null") {
    console.warn("[Preferences] Attempted to sync empty updates, skipping");
    return;
  }

  try {
    const response = await fetch("/api/user/preferences", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body,
    });

    if (response.status === 429) {
      // Rate limited - re-queue updates and retry after delay
      const retryAfter = Number(response.headers.get("Retry-After")) || 60;
      console.warn(`Rate limited. Retrying preference sync in ${retryAfter}s`);
      pendingUpdates = { ...updates, ...pendingUpdates };
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        flushPendingUpdates();
      }, retryAfter * 1000);
      return;
    }

    if (!response.ok) {
      console.error("Failed to save preferences to server");
    }
  } catch (error) {
    console.error("Error saving preferences to server:", error);
  }
}

function queuePreferenceUpdate(update: Partial<UserPreferences>) {
  // Merge with pending updates
  pendingUpdates = { ...pendingUpdates, ...update };

  // Reset debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    flushPendingUpdates();
  }, DEBOUNCE_MS);
}

/**
 * Action atom to save a preference to the server (debounced)
 */
export const savePreferenceToServerAtom = atom(
  null,
  async (_get, _set, update: Partial<UserPreferences>) => {
    queuePreferenceUpdate(update);
  },
);

/**
 * Helper function to create a preference atom that syncs with server
 */
function createPreferenceAtom<K extends keyof UserPreferences>(key: K) {
  return atom(
    (get) => get(preferencesAtom)[key],
    (get, set, value: UserPreferences[K]) => {
      // Update local state immediately
      const current = get(preferencesAtom);
      set(preferencesAtom, { ...current, [key]: value });

      // Queue debounced sync to server
      set(savePreferenceToServerAtom, { [key]: value });
    },
  );
}

// Individual preference atoms with automatic server sync
export const showCreditsInSidebarAtom = createPreferenceAtom(
  "showCreditsInSidebar",
);
export const performanceModeAtom = createPreferenceAtom("performanceMode");
export const snapToGridAtom = createPreferenceAtom("snapToGrid");
export const rememberAssetFiltersAtom = createPreferenceAtom(
  "rememberAssetFilters",
);
export const showPromptBelowImageAtom = createPreferenceAtom(
  "showPromptBelowImage",
);
export const simplifiedSidebarAtom = createPreferenceAtom("simplifiedSidebar");
export const themeStyleAtom = createPreferenceAtom("themeStyle");
export const dashboardV2Atom = createPreferenceAtom("dashboardV2");
export const focusNewNodesAtom = createPreferenceAtom("focusNewNodes");
export const autoConnectNewNodesAtom = createPreferenceAtom(
  "autoConnectNewNodes",
);
export const autoArrangeOnlyConnectedAtom = createPreferenceAtom(
  "autoArrangeOnlyConnected",
);
export const autoPlayVideosOnCanvasAtom = createPreferenceAtom(
  "autoPlayVideosOnCanvas",
);
export const defaultCanvasAspectRatioAtom = createPreferenceAtom(
  "defaultCanvasAspectRatio",
);
export const lastEditModeAtom = createPreferenceAtom("lastEditMode");
export const collapseSidebarOnCanvasAtom = createPreferenceAtom(
  "collapseSidebarOnCanvas",
);
export const defaultImageGenModelAtom = createPreferenceAtom(
  "defaultImageGenModel",
);
export const defaultVideoProviderAtom = createPreferenceAtom(
  "defaultVideoProvider",
);
export const defaultEditProviderAtom = createPreferenceAtom(
  "defaultEditProvider",
);
export const draftVoiceModelAtom = createPreferenceAtom("draftVoiceModel");
export const useOriginalsForPlaytestAtom = createPreferenceAtom(
  "useOriginalsForPlaytest",
);
export const fastPublishingAtom = createPreferenceAtom("fastPublishing");

// ============================================
// Brainstorm Settings Atom
// ============================================

/**
 * Brainstorm settings atom with automatic server sync
 * Updates the entire brainstorm settings object at once
 */
export const brainstormSettingsAtom = atom(
  (get) => {
    const brainstorm = get(preferencesAtom).brainstorm;
    // A stored legacy model (Flash v2.5 was the old default) is used as
    // Eleven v4 for new generations (voice programme DV1).
    const ttsModel = resolveVoiceModelForNewGeneration(brainstorm.ttsModel);
    return ttsModel === brainstorm.ttsModel
      ? brainstorm
      : { ...brainstorm, ttsModel };
  },
  (
    get,
    set,
    value:
      | BrainstormSettings
      | ((prev: BrainstormSettings) => BrainstormSettings),
  ) => {
    const current = get(preferencesAtom);
    const newBrainstorm =
      typeof value === "function" ? value(current.brainstorm) : value;

    // Skip if no actual change (reference equality check for functional updates)
    if (newBrainstorm === current.brainstorm) {
      return;
    }

    // Update local state immediately
    set(preferencesAtom, { ...current, brainstorm: newBrainstorm });

    // Queue debounced sync to server
    set(savePreferenceToServerAtom, { brainstorm: newBrainstorm });
  },
);

/**
 * Helper to get preset mode from current settings
 */
export function getPresetModeFromSettings(
  aiModel: AIModel,
  ttsModel: TTSModel,
): PresetMode {
  if (
    aiModel === MODEL_PRESETS.speed.aiModel &&
    ttsModel === MODEL_PRESETS.speed.ttsModel
  ) {
    return "speed";
  }
  if (
    aiModel === MODEL_PRESETS.quality.aiModel &&
    ttsModel === MODEL_PRESETS.quality.ttsModel
  ) {
    return "quality";
  }
  return "custom";
}

// ============================================
// Settings Dialog State (Global)
// ============================================

export type SettingsTab =
  | "profile"
  | "plans"
  | "people"
  | "preferences"
  | "referrals";

interface SettingsDialogState {
  isOpen: boolean;
  defaultTab: SettingsTab;
}

/**
 * Global settings dialog state atom
 * Allows any component to open the settings dialog
 */
export const settingsDialogAtom = atom<SettingsDialogState>({
  isOpen: false,
  defaultTab: "preferences",
});

/**
 * Action atom to open the settings dialog to a specific tab
 */
export const openSettingsDialogAtom = atom(
  null,
  (_get, set, tab: SettingsTab = "preferences") => {
    set(settingsDialogAtom, { isOpen: true, defaultTab: tab });
  },
);

/**
 * Action atom to close the settings dialog
 */
export const closeSettingsDialogAtom = atom(null, (_get, set) => {
  set(settingsDialogAtom, (prev) => ({ ...prev, isOpen: false }));
});

/**
 * Action atom to reset all preferences to defaults and sync to server
 */
export const resetPreferencesToDefaultsAtom = atom(null, (_get, set) => {
  set(preferencesAtom, DEFAULT_PREFERENCES);
  set(savePreferenceToServerAtom, DEFAULT_PREFERENCES);
});
